<template>
  <div class="chat-group-history">
    <div class="chat-group-history__head">
      <div class="chat-group-history__head-info">
        <el-image
            v-if="group && group.avatar"
            class="chat-group-history__avatar"
            :src="buildFileUrl(group.avatar)"
            fit="cover"
        ></el-image>
        <div v-else class="chat-group-history__avatar chat-group-history__avatar--empty">
          <i class="el-icon-user-solid"></i>
        </div>
        <div class="chat-group-history__title">
          <div class="chat-group-history__name">{{ group ? group.groupName : '' }}</div>
          <div class="chat-group-history__subtitle">聊天记录（只读）</div>
        </div>
      </div>
      <el-button type="text" icon="el-icon-close" class="chat-group-history__close" @click="$emit('close')"></el-button>
    </div>

    <div class="chat-group-history__body-wrap">
      <div
          v-loading="messageLoading"
          ref="messageScroll"
          class="chat-group-history__body"
          @scroll="handleMessageScroll"
      >
      <div v-if="!messageLoading && messageList.length === 0" class="chat-group-history__empty">暂无聊天记录</div>
      <template v-else>
        <div v-if="messageLoadingMore" class="chat-group-history__history-tip">加载中...</div>
        <div v-else-if="messageIsAll" class="chat-group-history__history-tip">没有更多历史消息了</div>
        <div
            v-for="(msg, index) in messageList"
            :key="msg.id || index"
            class="chat-group-history__item"
        >
          <div class="chat-group-history__item-head">
            <el-image
                class="chat-group-history__item-avatar"
                :src="buildMessageAvatar(msg)"
                fit="cover"
            ></el-image>
            <div class="chat-group-history__item-meta">
              <span class="chat-group-history__item-name">{{ msg.nickname || '群成员' }}</span>
              <span
                  v-if="msg.showRoleTag"
                  class="chat-group-history__role-tag"
                  :class="msg.roleTagClass"
              >{{ msg.roleTagLabel }}</span>
              <span class="chat-group-history__item-time">{{ formatDateTime(msg.sendTime) }}</span>
            </div>
          </div>
          <div class="chat-group-history__item-body">
            <div v-if="msg.contentType === '0'" class="chat-group-history__text">{{ msg.content }}</div>
            <div v-else-if="msg.contentType === '1'" class="chat-group-history__media">
              <el-image
                  :src="buildFileUrl(msg.content)"
                  :preview-src-list="[buildFileUrl(msg.content)]"
                  fit="cover"
                  style="width: 120px; height: 120px;"
              ></el-image>
            </div>
            <div v-else-if="msg.contentType === '2'" class="chat-group-history__media">
              <video controls :src="buildFileUrl(msg.content)" style="max-width: 240px;"></video>
            </div>
            <div v-else-if="msg.contentType === '3'" class="chat-group-history__media">
              <audio controls :src="buildFileUrl(msg.content)" style="max-width: 240px;"></audio>
            </div>
            <div v-else-if="msg.contentType === '4'" class="chat-group-history__product">
              <message-product :product-str="msg.content" :width="280" :height="90"></message-product>
            </div>
            <div v-else class="chat-group-history__text">{{ msg.content || '[不支持的消息类型]' }}</div>
          </div>
        </div>
      </template>
      </div>
      <div
          v-if="newMessageCount > 0"
          class="chat-group-history__new-msg-tip"
          @click="scrollToNewMessages"
      >
        {{ newMessageCount > 1 ? `${newMessageCount}条新消息` : '有新消息' }}
        <i class="el-icon-arrow-down"></i>
      </div>
    </div>
  </div>
</template>

<script>
import agencyListChatGroupMessage from '@/api/chat/chatGroupMessage/agencyList'
import MessageProduct from '@/components/MessageProduct/MessageProduct.vue'
import { resolveRoleTag } from '../js/chatGroupRoleUtils'
import {
  registerGroupMessageCallback,
  unregisterGroupMessageCallback,
  registerGroupDeleteCallback,
  unregisterGroupDeleteCallback,
} from '@/views/CustomerMessageManage/handler/MessageHandler'

const DEFAULT_AVATAR = 'https://thirdwx.qlogo.cn/mmopen/vi_32/POgEwh4mIHO4nibH0KlMECNjjGxQUq24ZEaGT4poC6icRiccVGKSyXwibcPq4BWmiaIGuG1icwxaQX6grC9VemZoJ8rg/132'

export default {
  name: 'ChatGroupHistoryPanel',
  components: { MessageProduct },
  props: {
    group: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      fileBaseUrl: process.env.VUE_APP_FILE_BASE_URL,
      messageList: [],
      messageLoading: false,
      messageLoadingMore: false,
      messageIsAll: false,
      newMessageCount: 0,
    }
  },
  watch: {
    'group.id': {
      immediate: true,
      handler() {
        this.resetAndLoad()
      },
    },
  },
  mounted() {
    registerGroupMessageCallback(this.handleGroupMessage)
    registerGroupDeleteCallback(this.handleGroupMessageDelete)
  },
  beforeDestroy() {
    unregisterGroupMessageCallback(this.handleGroupMessage)
    unregisterGroupDeleteCallback(this.handleGroupMessageDelete)
  },
  methods: {
    isSameGroup(groupId) {
      return this.group && Number(this.group.id) === Number(groupId)
    },
    handleGroupMessage(message) {
      if (!message || message.groupId == null || !this.isSameGroup(message.groupId)) {
        return
      }
      this.appendMessage(message)
    },
    handleGroupMessageDelete(message) {
      if (!message || message.groupId == null || message.messageId == null || !this.isSameGroup(message.groupId)) {
        return
      }
      this.removeMessage(message.messageId)
    },
    appendMessage(message) {
      if (!message) {
        return
      }
      const normalized = this.normalizeMessage(message)
      if (normalized.id && this.messageList.some(item => item.id === normalized.id)) {
        return
      }
      this.messageList.push(normalized)
      this.messageList.sort((a, b) => this.parseSendTime(a.sendTime) - this.parseSendTime(b.sendTime))
      this.$nextTick(() => {
        const el = this.$refs.messageScroll
        if (this.isScrolledUpBeyondScreen(el)) {
          this.newMessageCount++
        } else {
          this.scrollToBottom()
        }
      })
    },
    scrollToNewMessages() {
      this.newMessageCount = 0
      this.scrollToBottom()
    },
    isScrolledUpBeyondScreen(el) {
      const target = el || this.$refs.messageScroll
      if (!target) {
        return false
      }
      const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight
      return distanceFromBottom > target.clientHeight
    },
    removeMessage(messageId) {
      if (messageId == null) {
        return
      }
      const index = this.messageList.findIndex(item => item.id === messageId)
      if (index >= 0) {
        this.messageList.splice(index, 1)
      }
    },
    resetAndLoad() {
      this.messageList = []
      this.messageIsAll = false
      this.newMessageCount = 0
      this.loadMessages(0, false)
    },
    loadMessages(sendTime, append) {
      if (!this.group || !this.group.id) {
        return
      }
      const el = append ? this.$refs.messageScroll : null
      const prevHeight = el ? el.scrollHeight : 0
      if (append) {
        this.messageLoadingMore = true
      } else {
        this.messageLoading = true
      }
      agencyListChatGroupMessage(this.group.id, sendTime || 0).then(res => {
        if (res.code !== 1) {
          this.$message.error(res.msg || '聊天记录加载失败')
          return
        }
        const list = (res.data || []).map(item => this.normalizeMessage(item))
        if (append) {
          if (!list.length) {
            this.messageIsAll = true
            return
          }
          this.messageList = list.concat(this.messageList)
          this.$nextTick(() => {
            if (el) {
              el.scrollTop = el.scrollHeight - prevHeight
            }
          })
        } else {
          this.messageList = list
        }
        if (list.length < 20) {
          this.messageIsAll = true
        }
        if (!append) {
          this.$nextTick(() => this.scrollToBottom())
        }
      }).finally(() => {
        this.messageLoading = false
        this.messageLoadingMore = false
      })
    },
    handleMessageScroll(e) {
      const el = e.target
      if (!this.isScrolledUpBeyondScreen(el)) {
        this.newMessageCount = 0
      }
      if (this.messageLoading || this.messageLoadingMore || this.messageIsAll || !this.messageList.length) {
        return
      }
      if (el.scrollTop <= 30) {
        this.loadMoreMessages()
      }
    },
    loadMoreMessages() {
      if (this.messageLoadingMore || this.messageIsAll || !this.messageList.length) {
        return
      }
      const first = this.messageList[0]
      this.loadMessages(this.parseSendTime(first.sendTime), true)
    },
    scrollToBottom() {
      const el = this.$refs.messageScroll
      if (el) {
        el.scrollTop = el.scrollHeight
      }
    },
    normalizeMessage(raw) {
      const senderRole = raw.senderRole != null ? Number(raw.senderRole) : null
      const roleTag = resolveRoleTag(senderRole, raw.senderRoleLabel || '')
      return {
        ...raw,
        contentType: this.resolveContentType(raw.contentType),
        content: raw.content != null ? String(raw.content) : '',
        ...roleTag,
      }
    },
    resolveContentType(contentType) {
      if (contentType == null) {
        return '0'
      }
      if (typeof contentType === 'object' && contentType.value != null) {
        return String(contentType.value)
      }
      return String(contentType)
    },
    parseSendTime(sendTime) {
      if (!sendTime) {
        return 0
      }
      if (typeof sendTime === 'number') {
        return sendTime
      }
      if (Array.isArray(sendTime)) {
        const [year, month, day, hour = 0, minute = 0, second = 0] = sendTime
        return new Date(year, month - 1, day, hour, minute, second).getTime()
      }
      const parsed = new Date(sendTime).getTime()
      return Number.isNaN(parsed) ? 0 : parsed
    },
    buildMessageAvatar(msg) {
      if (msg.headImage) {
        return this.buildFileUrl(msg.headImage)
      }
      return DEFAULT_AVATAR
    },
    buildFileUrl(path) {
      if (!path) {
        return ''
      }
      return this.fileBaseUrl + (path.startsWith('/') ? path : '/' + path)
    },
    formatDateTime(value) {
      if (!value) {
        return '—'
      }
      if (typeof value === 'number') {
        const date = new Date(value)
        const pad = n => String(n).padStart(2, '0')
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
      }
      if (Array.isArray(value)) {
        const [year, month, day, hour = 0, minute = 0, second = 0] = value
        const pad = n => String(n).padStart(2, '0')
        return `${year}-${pad(month)}-${pad(day)} ${pad(hour)}:${pad(minute)}:${pad(second)}`
      }
      return String(value).replace('T', ' ')
    },
  },
}
</script>

<style lang="scss" scoped>
.chat-group-history {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 1px solid #ebeef5;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: #484a4b;
    color: #18faeb;
    flex-shrink: 0;
  }

  &__head-info {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 6px;
    flex-shrink: 0;

    &--empty {
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.15);
      color: #fff;
      font-size: 18px;
    }
  }

  &__title {
    min-width: 0;
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__subtitle {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.65);
    margin-top: 2px;
  }

  &__close {
    color: #fff !important;
    font-size: 18px;
    padding: 4px;
    flex-shrink: 0;
  }

  &__body-wrap {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  &__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 12px 16px;
  }

  &__new-msg-tip {
    position: absolute;
    left: 50%;
    bottom: 12px;
    transform: translateX(-50%);
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 14px;
    border-radius: 999px;
    background: #fff;
    color: #409eff;
    font-size: 12px;
    cursor: pointer;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
    user-select: none;

    &:hover {
      background: #ecf5ff;
    }
  }

  &__empty {
    padding: 48px 0;
    text-align: center;
    color: #909399;
  }

  &__history-tip {
    text-align: center;
    margin-bottom: 12px;
    font-size: 12px;
    color: #909399;
    padding: 4px 0;
  }

  &__item {
    padding: 12px 0;
    border-bottom: 1px solid #ebeef5;

    &:last-child {
      border-bottom: none;
    }
  }

  &__item-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
  }

  &__item-avatar {
    width: 36px;
    height: 36px;
    border-radius: 6px;
    flex-shrink: 0;
    background: #f5f7fa;
  }

  &__item-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    min-width: 0;
  }

  &__item-name {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
  }

  &__role-tag {
    flex-shrink: 0;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 11px;
    line-height: 1.4;

    &--owner {
      color: #fff;
      background: #f56c6c;
    }

    &--admin {
      color: #fff;
      background: #e6a23c;
    }
  }

  &__item-time {
    font-size: 12px;
    color: #909399;
  }

  &__item-body {
    padding-left: 46px;
  }

  &__text {
    font-size: 14px;
    line-height: 1.6;
    color: #303133;
    white-space: pre-wrap;
    word-break: break-all;
  }

  &__media {
    line-height: 0;
  }

  &__product {
    max-width: 280px;
  }
}
</style>
